import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { EkButtonComponent } from '@beta-foundations/ui-kit/buttons';
import {
    EkDialogBodyComponent,
    EkDialogFooterComponent,
    EkDialogHeaderComponent
} from '@beta-foundations/ui-kit/overlay/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { combineLatest, Observable, take } from 'rxjs';
import { EkGenericTableColumn, EkTable, EkTableEmptyState, IEkTableEmptyState } from '@beta-foundations/ui-kit/types';
import { EkTableComponent } from "@beta-foundations/ui-kit/table";
import { HttpResponseBase } from '@angular/common/http';
import { TableRowSelectEvent } from 'primeng/table';
import { InsegnamentoItem, InsegnamentoService } from '../../services/insegnamento.service';
import { CreateSubtenantModalComponent } from '../create-subtenant-modal/create-subtenant-modal.component';
import { DeleteMessageComponent } from '../../../features/documenti/delete-message/delete-message.component';
import { SubtenantService } from '../../services/subtenant.service';
import { NotificationService } from '../../services/notification.service';
import { SettingsService } from '../../services/settings.service';
import { UserService } from '../../services/user.service';
import { User, UserGroup } from '../../models/user';

@Component({
    selector: 'app-cambio-insegnamento',
    standalone: true,
    imports: [ CommonModule,
        EkDialogHeaderComponent,
        EkDialogBodyComponent,
        EkDialogFooterComponent,
        TranslateModule,
        EkButtonComponent, EkTableComponent ],
    templateUrl: './cambio-insegnamento-modal.component.html',
    styleUrl: './cambio-insegnamento-modal.component.scss'
})
export class CambioInsegnamentoComponent implements OnInit {


    selectedItem$: Observable<InsegnamentoItem | null>;
    items: InsegnamentoItem[] | undefined;
    pendingSelectionId: string | number | undefined;
    selectedInsegnamentoId: string | number | null | undefined;
    selectedTableInsegnamento: any | undefined;
    isLoading: boolean = true;
    isDialogLoading: boolean = false;
    canManageSubtenants: boolean = false;

    public insegnamentiDataSource: EkTable = new EkTable();
    protected insegnamentiTableCols: EkGenericTableColumn[] = [
        "radio",
        "insegnamento",
        "classe",
        "btnIcons"
    ];

    protected EMPTY_STATE: IEkTableEmptyState = new EkTableEmptyState({
        title: "INSEGNAMENTO.EMPTY_STATE.TITLE",
        description: "INSEGNAMENTO.EMPTY_STATE.DESCRIPTION",
    });

    private createRef: DynamicDialogRef | undefined;
    private editRef: DynamicDialogRef | undefined;
    private deleteRef: DynamicDialogRef | undefined;

    constructor(
        public ref: DynamicDialogRef,
        private insegnamentoService: InsegnamentoService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private dialogService: DialogService,
        private subtenantService: SubtenantService,
        private notificationService: NotificationService,
        private translate: TranslateService,
        private settingsService: SettingsService,
        private userService: UserService
    ) {
        this.selectedItem$ = this.insegnamentoService.selectedTeaching$;
    }

    ngOnInit(): void {
        combineLatest([
            this.settingsService.isFeatureEnabled('subtenant', 'PERMISSIONS'),
            this.userService.getUser()
        ])
            .pipe(take(1))
            .subscribe({
                next: ([enabled, user]) => {
                    this.canManageSubtenants = enabled && this.isSubtenantManager(user);
                    this.loadSelectedTeaching();
                },
                error: () => {
                    this.canManageSubtenants = false;
                    this.loadSelectedTeaching();
                }
            });
    }

    private isSubtenantManager(user: User | null): boolean {
        return user?.group === UserGroup.SYS_ADMIN || user?.group === UserGroup.ADMIN;
    }

    private loadSelectedTeaching(): void {
        this.selectedItem$.pipe(
          take(1)
        ).subscribe(item => {
            this.selectedInsegnamentoId = item?.id;
            this.pendingSelectionId = item?.id;
            this.cercaInsegnamenti();
        });
    }

    mappaInsegnamentiARiga(ins: InsegnamentoItem): any {
        const rowId = ins.id || "";
        return {
            id: rowId,
            styleClass: '',
            data: {
                insegnamento: ins.insegnamento,
                classe: ins.classeLaurea
            },
            insegnamentiDataSource: ins,
            selected: rowId !== "" && rowId === this.selectedInsegnamentoId,
            accordion: false,
            arrowDropdown: false,
            expand: false,
            disabled: false,
            dropdownMenu: [],
            btnIcons: this.canManageSubtenants ? [
                { id: 'edit', icon: 'edit' },
                { id: 'delete', icon: 'delete', severity: 'danger' }
            ] : [],
        };
    }

    popolaTabellaInsegnamenti(insegnamenti: InsegnamentoItem[]): any[] {
        return insegnamenti.map((ins) => this.mappaInsegnamentiARiga(ins));
    }

    cercaInsegnamenti() {
        this.insegnamentoService.teachings$.subscribe({
            next: (listaInsegnamenti: InsegnamentoItem[]) => {
                this.insegnamentiDataSource = new EkTable({
                    id: "table-insegnamenti",
                    columns: [ ...this.insegnamentiTableCols ],
                    columnsHeaders: {},
                    columnsClass: {
                        radio: {
                            flexBasis: '3rem',
                            flexGrow: 0,
                            classes: []
                        },
                        insegnamento: {
                            flexBasis: '20rem',
                            flexGrow: 1,
                            classes: []
                        },
                        classe: {
                            flexBasis: '20rem',
                            flexGrow: 0,
                            classes: []
                        },
                        btnIcons: {
                            flexBasis: '8rem',
                            flexGrow: 0,
                            classes: ['justify-content-end']
                        },
                    },
                    rows: this.popolaTabellaInsegnamenti(listaInsegnamenti),
                });

                const initialRow = this.insegnamentiDataSource.rows.find(
                  (row: any) => row.id == this.selectedInsegnamentoId
                );
                this.selectedTableInsegnamento = initialRow;

                this.updatePendingSelection();

                setTimeout(() => {
                    this.isLoading = false;
                    this.cdr.detectChanges();
                }, 0);

            },
            error: (error: HttpResponseBase) => {
                this.isLoading = false;
            },
        });
    }

    /**
     * Conferma la selezione e aggiorna il servizio.
     */
    confirmSelection(): void {
        if (this.pendingSelectionId === undefined || this.isDialogLoading) {
            this.ref.close();
            return;
        }
        this.isDialogLoading = true;
        this.insegnamentoService.selectInsegnamento(this.pendingSelectionId).subscribe({
            next: () => {
                this.isDialogLoading = false;
                this.ref.close();
                this.reloadCurrentRoute();
            },
            error: () => {
                this.isDialogLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    private reloadCurrentRoute(): void {
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true })
            .then(() => this.router.navigateByUrl(currentUrl));
    }

    updatePendingSelection(): void {
        this.pendingSelectionId = this.selectedTableInsegnamento?.id;
    }

    /**
     * Aggiorna l'ID in attesa quando l'utente seleziona un radio button.
     */
    onSelectionChange(event: TableRowSelectEvent): void {
        this.pendingSelectionId = event.data?.id;
    }

    /**
     * Restituisce TRUE se il pulsante "Cambia" deve essere disabilitato.
     * Disabilitato se:
     * 1. Non c'è una selezione valida.
     * 2. La selezione attuale (pending) è IDENTICA a quella originale (selected).
     */
    isConfirmDisabled(): boolean {
        if (this.isLoading) {
            return true;
        }
        const pendingStr = String(this.pendingSelectionId);
        const originalStr = String(this.selectedInsegnamentoId);
        return pendingStr === originalStr;
    }

    openCreateModal(): void {
        if (!this.canManageSubtenants) {
            return;
        }
        this.createRef = this.dialogService.open(CreateSubtenantModalComponent, {
            width: "720px",
            contentStyle: { overflow: "auto" },
            baseZIndex: 10000,
            maximizable: false,
        });

        this.createRef.onClose.pipe(take(1)).subscribe((result) => {
            if (result?.action === 'created') {
                this.handleSubtenantCreated(result.subtenant?.id);
            }
        });
    }

    private handleSubtenantCreated(newId: string | number | undefined): void {
        this.isLoading = true;
        this.insegnamentoService.reloadTeachings().pipe(take(1)).subscribe({
            next: () => {
                if (newId !== undefined) {
                    this.pendingSelectionId = newId;
                    const newRow = this.insegnamentiDataSource.rows.find(
                        (row: any) => String(row.id) === String(newId)
                    );
                    if (newRow) {
                        this.selectedTableInsegnamento = newRow;
                    }
                }
                this.isLoading = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.isLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    onRowBtnClick(event: { iconId: string; rowId: string }): void {
        if (!this.canManageSubtenants) {
            return;
        }
        const row = this.insegnamentiDataSource.rows.find(
            (r: any) => String(r.id) === String(event.rowId)
        );
        const item: InsegnamentoItem | undefined = (row as any)?.insegnamentiDataSource;
        if (!item) {
            return;
        }

        if (event.iconId === 'edit') {
            this.openEditModal(item);
        } else if (event.iconId === 'delete') {
            this.confirmDelete(item);
        }
    }

    private openEditModal(item: InsegnamentoItem): void {
        this.editRef = this.dialogService.open(CreateSubtenantModalComponent, {
            width: "720px",
            contentStyle: { overflow: "auto" },
            baseZIndex: 10000,
            maximizable: false,
            data: {
                subtenant: {
                    id: item.id,
                    name: item.tipoCorso,
                    degreeClass: item.classeLaurea,
                    departmentName: item.dipartimento,
                    locationName: item.sede,
                    address: item.indirizzo,
                    year: item.coorte
                }
            }
        });

        this.editRef.onClose.pipe(take(1)).subscribe((result) => {
            if (result?.action === 'updated') {
                this.reloadList();
            }
        });
    }

    private confirmDelete(item: InsegnamentoItem): void {
        this.deleteRef = this.dialogService.open(DeleteMessageComponent, {
            width: "400px",
            contentStyle: { overflow: "auto" },
            baseZIndex: 10000,
            maximizable: false,
            data: {
                title: 'LABELS.ATTENTION',
                message: this.translate.instant('INSEGNAMENTO.DELETE_MODAL.MESSAGE', {
                    name: item.tipoCorso || item.insegnamento
                })
            }
        });

        this.deleteRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.performDelete(item);
            }
        });
    }

    private performDelete(item: InsegnamentoItem): void {
        this.isLoading = true;
        this.subtenantService.deleteSubtenant(item.id).pipe(take(1)).subscribe({
            next: () => {
                this.notificationService.success(
                    this.translate.instant('INSEGNAMENTO.DELETE_MODAL.SUCCESS', {
                        name: item.tipoCorso || item.insegnamento
                    })
                );
                if (String(item.id) === String(this.pendingSelectionId)) {
                    this.pendingSelectionId = this.selectedInsegnamentoId ?? undefined;
                }
                this.reloadList();
            },
            error: (error) => {
                this.isLoading = false;
                this.notificationService.showBackendError(
                    error,
                    this.translate.instant('INSEGNAMENTO.DELETE_MODAL.ERROR')
                );
                this.cdr.detectChanges();
            }
        });
    }

    private reloadList(): void {
        this.isLoading = true;
        this.insegnamentoService.reloadTeachings().pipe(take(1)).subscribe({
            next: () => {
                this.isLoading = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.isLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    /**
     * Chiude la modale senza apportare modifiche.
     */
    close(): void {
        this.ref.close();
    }
}
